import React, { useState, useEffect, useRef } from "react";
import Card from "components/card";
import { MdSearch, MdError, MdCheckCircle, MdPending, MdClose, MdQrCode } from "react-icons/md";
import { Toaster, toast } from 'react-hot-toast';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";

const Loader = () => (
  <div className="flex h-[400px] items-center justify-center">
    <div className="relative">
      <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
      <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
      <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
      <div className="mt-8 text-center">
        <p className="text-lg font-medium text-gray-600 dark:text-white">Loading</p>
        <p className="text-sm text-gray-400">Please wait...</p>
      </div>
    </div>
  </div>
);

const Consume = () => {
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedZoneForQR, setSelectedZoneForQR] = useState(null);
  const [showQRPreview, setShowQRPreview] = useState(false);
  const qrCodeRef = useRef();

  useEffect(() => {
    const fetchZones = async () => { 
      try {
        setLoading(true);
        const response = await axios.get("https://usibras-web-backend.onrender.com/api/stack-zone-data");
        
        // Transform the API data to match the UI structure
        const transformedZones = response.data.data.map((zone, index) => ({
          id: index + 1,
          name: zone.zone,
          totalWeight: zone.total_weight,
          cashews: zone.origins.flatMap(origin =>
            origin.sizes.map(sizeEntry => ({
              size: `Size ${sizeEntry.size.split('_')[1]}`,
              weight: sizeEntry.weight,
              origin: origin.origin
            }))
          )
        }));

        setZones(transformedZones);
      } catch (err) {
        setError(err.message || "Failed to fetch zones");
        toast.error("Failed to fetch zones data");
      } finally {
        setLoading(false);
      }
    };

    fetchZones();
  }, []);

  const handleConsume = (zone) => {
    navigate('/admin/warehouse/consume/form', { state: { zone } });
  };

  const handleViewMore = (zone) => {
    navigate(`/admin/warehouse/zone-details/${zone.name}`, { state: { zone } });
  };

  const calculateTotalWeight = (cashews) => {
    if (!cashews || !Array.isArray(cashews)) return 0;
    return cashews.reduce((total, cashew) => total + cashew.weight, 0);
  };

  const getOriginsSummary = (cashews) => {
    if (!cashews || !Array.isArray(cashews)) return [];
    const origins = [...new Set(cashews.map(c => c.origin))];
    return origins.map(origin => ({
      origin,
      totalWeight: cashews
        .filter(c => c.origin === origin)
        .reduce((sum, c) => sum + c.weight, 0)
    }));
  };

  // Filter zones based on search query
  const filteredZones = zones.filter(zone => 
    zone.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Function to generate QR code for a zone
  const generateZoneQRCode = (zone) => {
    return new Promise((resolve, reject) => {
      setSelectedZoneForQR(zone);
      
      setTimeout(() => {
        if (qrCodeRef.current) {
          const svgElement = qrCodeRef.current.querySelector('svg');
          
          if (svgElement) {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            canvas.width = 400;
            canvas.height = 500;
            
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            
            const svgData = new XMLSerializer().serializeToString(svgElement);
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const DOMURL = window.URL || window.webkitURL || window;
            const img = new Image();
            const svgUrl = DOMURL.createObjectURL(svgBlob);
            
            img.onload = function() {
              ctx.drawImage(img, 80, 70, 240, 240);
              
              ctx.fillStyle = 'black';
              ctx.font = 'bold 24px Arial';
              ctx.textAlign = 'center';
              ctx.fillText('Zone QR Code', canvas.width/2, 40);
              
              ctx.fillStyle = '#f8f8f8';
              ctx.fillRect(50, 330, canvas.width - 100, 150);
              
              ctx.fillStyle = 'black';
              ctx.font = 'bold 20px Arial';
              ctx.fillText(`Stacking Zone ${zone.name}`, canvas.width/2, 400);
              
              ctx.font = '16px Arial';
              ctx.fillText('Scan to view zone details', canvas.width/2, 480);
              
              canvas.toBlob(function(blob) {
                const url = URL.createObjectURL(blob);
                resolve({
                  url,
                  filename: `Zone-${zone.name}-QRCode.png`
                });
              });
              
              DOMURL.revokeObjectURL(svgUrl);
            };
            
            img.src = svgUrl;
          } else {
            reject(new Error('SVG element not found'));
          }
        } else {
          reject(new Error('QR code ref not found'));
        }
      }, 100);
    });
  };

  // Function to handle QR code preview
  const handleQRCodePreview = (zone) => {
    setSelectedZoneForQR(zone);
    setShowQRPreview(true);
  };

  // Function to handle QR code download
  const handleQRCodeDownload = async () => {
    try {
      const { url, filename } = await generateZoneQRCode(selectedZoneForQR);
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = filename;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(url);
      toast.success('QR code downloaded successfully');
      setShowQRPreview(false);
    } catch (error) {
      console.error('Error generating QR code:', error);
      toast.error('Failed to generate QR code');
    }
  };

  return (
    <div className="mt-5 h-full">
      <Toaster position="top-right" />
      <Card extra={"w-full h-full p-6"}>
        {loading ? (
          <Loader />
        ) : error ? (
          <div className="flex h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-5xl text-red-500">
                <MdError className="mx-auto h-16 w-16" />
              </div>
              <p className="text-xl font-semibold text-gray-700 dark:text-white">
                Oops! Something went wrong
              </p>
              <p className="mt-2 text-gray-500 dark:text-gray-400">{error}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Header and Search */}
            <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <h4 className="text-2xl font-bold text-navy-700 dark:text-white">
                    Warehouse Zones
                  </h4>
                  <span className="rounded-full bg-brand-500 px-3 py-1.5 text-sm font-medium text-white shadow-sm">
                    {filteredZones.length} zones
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="flex items-center gap-1">
                    <svg className="h-4 w-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                    </svg>
                    <span className="text-gray-500 dark:text-gray-400">Total Weight:</span>
                    <span className="font-semibold text-blue-600 dark:text-blue-400">
                      {filteredZones.reduce((total, zone) => total + calculateTotalWeight(zone.cashews), 0)} kg
                    </span>
                  </span>
                </div>
              </div>
              <div className="flex w-full items-center rounded-xl border bg-white px-4 py-2.5 shadow-sm transition-all duration-200 hover:shadow-md dark:bg-navy-700 md:w-[420px]">
                <MdSearch className="h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search zones..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="ml-2 w-full bg-transparent text-sm font-medium text-gray-600 outline-none placeholder:text-gray-400 dark:text-white"
                />
              </div>
            </div>

            {/* Zones Grid */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filteredZones.map((zone) => (
                <Card
                  key={zone.id}
                  extra="p-5 hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-navy-700"
                >
                  <div className="flex flex-col gap-5">
                    {/* Zone Header */}
                    <div className="flex items-center justify-between">
                      <span className="rounded-full bg-brand-50 px-4 py-1.5 text-sm font-semibold text-brand-500">
                        Zone {zone.name}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleQRCodePreview(zone)}
                          className="rounded-lg p-1.5 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-600"
                          title="View QR Code"
                        >
                          <MdQrCode className="h-5 w-5" />
                        </button>
                        <span className="rounded-full bg-gray-50 px-4 py-1.5 text-sm font-medium text-gray-600 dark:bg-navy-700 dark:text-gray-300">
                          {zone.cashews.length} batches
                        </span>
                      </div>
                    </div>

                    {/* Zone Content */}
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Total Weight</span>
                        <span className="font-semibold text-navy-700 dark:text-white text-lg">
                          {calculateTotalWeight(zone.cashews)} kg
                        </span>
                      </div>
                      <div className="space-y-2">
                        <span className="text-sm text-gray-500">Origins</span>
                        <div className="flex flex-wrap gap-2">
                          {getOriginsSummary(zone.cashews).map((origin) => (
                            <span
                              key={origin.origin}
                              className="rounded-lg bg-gray-50 px-3 py-1 text-sm font-medium text-gray-600 dark:bg-navy-700 dark:text-gray-300"
                            >
                              {origin.origin}: {origin.totalWeight} kg
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 mt-2">
                      <button
                        onClick={() => handleViewMore(zone)}
                        className="flex-1 rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-600 transition-all duration-200 hover:bg-gray-200 hover:shadow-md dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => handleConsume(zone)}
                        className="flex-1 rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-brand-600 hover:shadow-lg"
                      >
                        Consume
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* No Results Message */}
            {filteredZones.length === 0 && (
              <div className="mt-6 flex h-[200px] items-center justify-center rounded-xl border border-gray-200 bg-gray-50 dark:border-navy-700 dark:bg-navy-800">
                <p className="text-center text-gray-500 dark:text-gray-400">
                  No zones found matching your search
                </p>
              </div>
            )}
          </>
        )}
      </Card>

      {/* QR Code Preview Modal */}
      {showQRPreview && selectedZoneForQR && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/30">
          <div className="relative w-[420px] rounded-2xl bg-white p-8 shadow-2xl dark:bg-navy-700 transform transition-all duration-300 ease-in-out">
            <button
              onClick={() => setShowQRPreview(false)}
              className="absolute right-4 top-4 rounded-full p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-navy-600 transition-colors duration-200"
            >
              <MdClose className="h-5 w-5" />
            </button>
            <div className="flex flex-col items-center gap-6">
              <div className="flex flex-col items-center gap-2">
                <h3 className="text-2xl font-bold text-navy-700 dark:text-white">Zone QR Code</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Scan to view zone details</p>
              </div>
              <div className="rounded-xl border-2 border-gray-100 bg-white p-6 shadow-lg dark:border-navy-600 dark:bg-navy-800">
                <QRCodeSVG 
                  value={`https://usibras-web.vercel.app/admin/warehouse/zone-details/${selectedZoneForQR.name}`}
                  size={240} 
                  level={"H"} 
                  includeMargin={true} 
                />
              </div>
              <div className="flex flex-col items-center gap-1">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Stacking Zone</span>
                <span className="text-xl font-bold text-navy-700 dark:text-white">{selectedZoneForQR.name}</span>
              </div>
              <button
                onClick={handleQRCodeDownload}
                className="w-full rounded-xl bg-brand-500 px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-brand-600 hover:shadow-lg active:scale-[0.98]"
              >
                Download QR Code
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden div for QR code generation */}
      {selectedZoneForQR && (
        <div style={{ display: 'none' }} ref={qrCodeRef}>
          <QRCodeSVG 
            value={`https://usibras-web.vercel.app/admin/warehouse/zone-details/${selectedZoneForQR.name}`}
            size={240} 
            level={"H"} 
            includeMargin={true} 
          />
        </div>
      )}
    </div>
  );
};

export default Consume; 